// Inlining for the creative image that goes into a downloadable article frame.
//
// html-to-image renders through an SVG foreignObject, so every image has to be
// embedded as a data URL. It will do that itself, but it swallows any fetch
// failure and substitutes an empty src, after which the SVG fails to load and
// the whole capture rejects with a bare event object - no message, no clue
// which resource was at fault. So we resolve the images up front instead: one
// fetch per unique URL, reused across gallery frames and cached for repeat
// downloads, and a precise error naming the host when one cannot be read.
//
// There is no client side way around a host that omits
// Access-Control-Allow-Origin, or whose redirect hop does: fetch is refused and
// drawing the <img> to a canvas taints it. Those fall back to the backend's
// proxy-download, which reads the file server side (see fetchViaProxy).

import JSZip from "jszip";

import config from "../config";

const dataUrlCache = new Map();

// Some hosts (Google Ad Manager's, for one) serve images as octet-stream.
// Browsers still draw those in an <img>, but the data URL is only as good as
// its declared type, so name the type from the file's own first bytes.
const sniffImageType = (bytes) => {
  const hex = Array.from(bytes.slice(0, 12), (b) => b.toString(16).padStart(2, "0")).join("");
  if (hex.startsWith("ffd8ff")) return "image/jpeg";
  if (hex.startsWith("89504e47")) return "image/png";
  if (hex.startsWith("47494638")) return "image/gif";
  if (hex.startsWith("52494646") && hex.slice(16, 24) === "57454250") return "image/webp";
  return "";
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

export class FrameImageError extends Error {
  constructor(url, cause) {
    let host = url;
    try {
      host = new URL(url, window.location.href).host;
    } catch (error) {
      // Leave host as the raw URL when it will not parse.
    }
    super(`Could not read image from ${host}`);
    this.name = "FrameImageError";
    this.url = url;
    this.host = host;
    this.cause = cause;
  }
}

const fetchAsDataUrl = async (url) => {
  const response = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  if (blob.type.startsWith("image/")) return blobToDataUrl(blob);

  const type = sniffImageType(new Uint8Array(await blob.slice(0, 12).arrayBuffer()));
  if (!type) throw new Error(`Not an image (${blob.type || "no content type"})`);
  return blobToDataUrl(blob.slice(0, blob.size, type));
};

// For hosts a browser cannot read directly: no CORS headers, or a redirect hop
// without them, which fails the whole request. The backend's proxy-download
// fetches the file server side, where neither matters, and answers with a zip.
const fetchViaProxy = async (url) => {
  const response = await fetch(`${config.BASE_URL}proxy-download`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url: [url], name: "frame-image" }),
  });
  if (!response.ok) throw new Error(`Proxy HTTP ${response.status}`);

  const zip = await JSZip.loadAsync(await response.arrayBuffer());
  const entry = Object.values(zip.files).find((file) => !file.dir);
  // The proxy skips a file it cannot fetch rather than failing, so an empty
  // zip is how a dead link shows up.
  if (!entry) throw new Error("Proxy could not fetch the image");

  const bytes = await entry.async("uint8array");
  const type = sniffImageType(bytes);
  if (!type) throw new Error("Proxy returned something that is not an image");
  return blobToDataUrl(new Blob([bytes], { type }));
};

/**
 * Resolve one image URL to a data URL, caching the in flight promise so
 * repeated frames share a single request.
 *
 * @throws {FrameImageError} when the host refuses a cross origin read.
 */
export const toDataUrl = async (url) => {
  if (!url) throw new FrameImageError(url);
  if (url.startsWith("data:")) return url;
  if (dataUrlCache.has(url)) return dataUrlCache.get(url);

  const pending = fetchAsDataUrl(url)
    .catch(() => fetchViaProxy(url))
    .catch((error) => {
      dataUrlCache.delete(url);
      throw new FrameImageError(url, error);
    });

  dataUrlCache.set(url, pending);
  return pending;
};

/**
 * Resolve every distinct URL in the list to a data URL.
 *
 * @returns {Promise<Map<string, string>>} original URL -> data URL
 */
export const resolveImages = async (urls) => {
  const unique = [...new Set(urls.filter(Boolean))];
  const entries = await Promise.all(
    unique.map(async (url) => [url, await toDataUrl(url)])
  );
  return new Map(entries);
};
